import Feature from 'ol/Feature.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style.js';
import { fromExtent as polygonFromExtent } from 'ol/geom/Polygon.js';
import Point from 'ol/geom/Point.js';
import MultiPoint from 'ol/geom/MultiPoint.js';
import LineString from 'ol/geom/LineString.js';
import MultiLineString from 'ol/geom/MultiLineString.js';
import Polygon from 'ol/geom/Polygon.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { mapService } from '@/services/map/mapService';

const HIGHLIGHT_ID = 'attribute-table-selection';
const geoJSON = new GeoJSON();
const makeHighlightStyle = (color, fill) => new Style({
  fill: new Fill({ color: fill }),
  stroke: new Stroke({ color, width: 4 }),
  image: new CircleStyle({ radius: 10, fill: new Fill({ color }), stroke: new Stroke({ color: '#ffffff', width: 3 }) }),
});
const orangeHighlight = makeHighlightStyle('#f08a00', 'rgba(240, 138, 0, 0.24)');
const cyanHighlight = makeHighlightStyle('#0284c7', 'rgba(14, 165, 233, 0.24)');

function isOrange(color) {
  if (color === 'orange') return true;
  const hex = typeof color === 'string' && color.match(/^#([\da-f]{3}|[\da-f]{6})$/i);
  let rgb = Array.isArray(color) ? color : null;
  if (hex) {
    const value = hex[1].length === 3 ? hex[1].split('').map((part) => part + part).join('') : hex[1];
    rgb = [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
  }
  if (!rgb && typeof color === 'string') {
    const values = color.match(/rgba?\(([^)]+)\)/i);
    if (values) rgb = values[1].split(',').slice(0, 3).map(Number);
  }
  return !!rgb && rgb[0] > 160 && rgb[1] > 55 && rgb[1] < 210 && rgb[2] < 100;
}

class AttributeSelectionService {
  constructor() { this.layer = null; this.map = null; this.onMapClick = null; this.layerId = null; this.store = null; this.unsubscribeStore = null; }

  start(store) {
    this.stopSync();
    this.store = store;
    this.unsubscribeStore = store?.subscribe((mutation) => {
      if (mutation.type === "removeActiveLayer" && String(mutation.payload?.id) === this.layerId) this.clear();
      else if (mutation.type === "cleanActiveLayers") this.clear();
    }) || null;
  }

  stopSync() {
    if (this.unsubscribeStore) this.unsubscribeStore();
    this.store = null;
    this.unsubscribeStore = null;
  }

  ensure() {
    const map = mapService.get();
    if (!map) return false;
    if (this.map === map && this.layer) return true;
    this.detach();
    this.map = map;
    this.layer = new VectorLayer({ source: new VectorSource(), style: orangeHighlight, zIndex: 10004 });
    this.layer.set('id', HIGHLIGHT_ID);
    map.addLayer(this.layer);
    this.onMapClick = (event) => {
      const hit = map.hasFeatureAtPixel(event.pixel, { layerFilter: (layer) => layer !== this.layer });
      if (!hit) this.clear();
    };
    map.on('singleclick', this.onMapClick);
    return true;
  }

  sourceFor(layer) {
    return mapService.getOlLayerById(layer?.id)?.getSource?.();
  }

  matchingFeature(layer, row) {
    const keys = Object.keys(row).filter((key) => !['geometry', 'bbox'].includes(key));
    return this.sourceFor(layer)?.getFeatures?.().find((feature) => keys.length && keys.every((key) => feature.get(key) === row[key]));
  }

  geometryFor(layer, row, metadata) {
    if (row.geometry && typeof row.geometry.clone === 'function') return row.geometry.clone();
    if (row.geometry?.type && row.geometry?.coordinates) {
      try {
        return geoJSON.readGeometry(row.geometry, {
          dataProjection: metadata?.crs || 'EPSG:3857',
          featureProjection: 'EPSG:3857',
        });
      } catch (_) { /* intenta los formatos ArcGIS y source local abajo */ }
    }
    if (Number.isFinite(row.geometry?.x) && Number.isFinite(row.geometry?.y)) return new Point([row.geometry.x, row.geometry.y]);
    if (Array.isArray(row.geometry?.points)) return new MultiPoint(row.geometry.points);
    if (Array.isArray(row.geometry?.paths)) {
      return row.geometry.paths.length === 1 ? new LineString(row.geometry.paths[0]) : new MultiLineString(row.geometry.paths);
    }
    if (Array.isArray(row.geometry?.rings)) return new Polygon(row.geometry.rings);
    const match = this.matchingFeature(layer, row);
    if (match?.getGeometry()) return match.getGeometry().clone();
    return Array.isArray(row.bbox) && row.bbox.length === 4 ? polygonFromExtent(row.bbox) : null;
  }

  highlight(layer, row, metadata) {
    if (!this.ensure()) return;
    this.layerId = layer?.id != null ? String(layer.id) : null;
    const geometry = this.geometryFor(layer, row, metadata);
    const feature = this.matchingFeature(layer, row);
    const styleFn = feature?.getStyleFunction?.() || mapService.getOlLayerById(layer?.id)?.getStyleFunction?.();
    const styles = styleFn ? styleFn(feature, this.map.getView().getResolution()) : [];
    const colors = (Array.isArray(styles) ? styles : [styles]).filter(Boolean).flatMap((style) => [style.getFill?.()?.getColor?.(), style.getStroke?.()?.getColor?.(), style.getImage?.()?.getFill?.()?.getColor?.(), style.getImage?.()?.getStroke?.()?.getColor?.()]);
    this.layer.setStyle(colors.some(isOrange) ? cyanHighlight : orangeHighlight);
    this.layer.getSource().clear();
    if (geometry) this.layer.getSource().addFeature(new Feature({ geometry }));
  }

  clear() {
    this.layerId = null;
    this.layer?.getSource().clear();
    window.dispatchEvent(new CustomEvent('attribute-table-selection-clear'));
  }

  detach() {
    if (this.map && this.onMapClick) this.map.un('singleclick', this.onMapClick);
    if (this.map && this.layer) this.map.removeLayer(this.layer);
    this.layer = null; this.map = null; this.onMapClick = null;
  }
}

export const attributeSelectionService = new AttributeSelectionService();
